import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Input, Select, Switch, TextArea } from "@andorhealth/clientuireact.designsystem";
import type { CatalogPolicy, CatalogRequestType } from "@/lib/catalog-api";
import { managementTenantKey } from "@/lib/management-api";
import { useConfiguration } from "../hooks/useConfiguration";
import { toPolicyCode } from "../lib/drafts";

function uniqueRequestCode(
  title: string,
  list: CatalogRequestType[],
  skipIndex?: number,
): string {
  const base = toPolicyCode(title) || "NEW_TYPE";
  const taken = new Set(
    list
      .filter((_, itemIndex) => itemIndex !== skipIndex)
      .map((item) => item.code.toUpperCase()),
  );
  if (!taken.has(base)) return base;
  let suffix = 2;
  while (taken.has(`${base}_${suffix}`)) suffix += 1;
  return `${base}_${suffix}`;
}

function coverageOptions(
  policies: CatalogPolicy[],
  assignedPolicyId: number | null | undefined,
) {
  return [
    { label: "Default coverage", value: "" },
    ...policies
      .filter(
        (policy) =>
          policy.escalationPolicyId > 0 &&
          (policy.isActive ||
            policy.escalationPolicyId === assignedPolicyId),
      )
      .map((policy) => ({
        label: policy.isActive
          ? policy.displayName
          : `${policy.displayName} (inactive)`,
        value: policy.escalationPolicyId,
      })),
  ];
}

export function RequestsStep() {
  const { requestTypes, setRequestTypes, catalog } = useConfiguration();
  const savedPolicies = catalog?.policies ?? [];

  return (
    <Stack spacing={2}>
      <Typography sx={{ color: "text.secondary" }}>
        Assignments can only use a coverage path that has already been saved.
      </Typography>
      {requestTypes.map((requestType, index) => (
        <Box
          key={`${requestType.requestTypeId}-${index}`}
          sx={{ border: "1px solid #e4e7ec", p: 2 }}
        >
          <Stack spacing={1.5}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Input
                size="small"
                label="Name"
                value={requestType.title}
                onChange={(event) => {
                  const title = event.target.value;
                  setRequestTypes((list) =>
                    list.map((item, itemIndex) => {
                      if (itemIndex !== index) return item;
                      if (item.requestTypeId > 0) return { ...item, title };
                      return {
                        ...item,
                        title,
                        code: uniqueRequestCode(title, list, index),
                      };
                    }),
                  );
                }}
                sx={{ flex: 1 }}
              />
              <Select
                label="Coverage"
                value={requestType.escalationPolicyId ?? ""}
                displayEmpty
                sx={{ flex: 1 }}
                options={coverageOptions(
                  savedPolicies,
                  requestType.escalationPolicyId,
                )}
                onChange={(event) =>
                  setRequestTypes((list) =>
                    list.map((item, itemIndex) =>
                      itemIndex === index
                        ? {
                            ...item,
                            escalationPolicyId:
                              String(event.target.value) === ""
                                ? null
                                : Number(event.target.value),
                          }
                        : item,
                    ),
                  )
                }
              />
            </Stack>
            <Select
              label="Priority"
              value={requestType.priority ?? "Routine"}
              sx={{ flex: 1 }}
              options={(catalog?.priorityOptions ?? []).map((option) => ({
                label: option.label,
                value: option.value,
              }))}
              onChange={(event) =>
                setRequestTypes((list) =>
                  list.map((item, itemIndex) =>
                    itemIndex === index
                      ? { ...item, priority: String(event.target.value) }
                      : item,
                  ),
                )
              }
            />
            <TextArea
              size="small"
              label="What the patient hears"
              value={requestType.confirmationTemplate}
              minRows={2}
              onChange={(event) =>
                setRequestTypes((list) =>
                  list.map((item, itemIndex) =>
                    itemIndex === index
                      ? { ...item, confirmationTemplate: event.target.value }
                      : item,
                  ),
                )
              }
            />
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={requestType.isPatientSelectable}
                    onChange={(event) =>
                      setRequestTypes((list) =>
                        list.map((item, itemIndex) =>
                          itemIndex === index
                            ? {
                                ...item,
                                isPatientSelectable: event.target.checked,
                              }
                            : item,
                        ),
                      )
                    }
                  />
                }
                label="Patients can ask for this"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={requestType.isEnabled}
                    onChange={(event) =>
                      setRequestTypes((list) =>
                        list.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, isEnabled: event.target.checked }
                            : item,
                        ),
                      )
                    }
                  />
                }
                label="On"
              />
            </Stack>
          </Stack>
        </Box>
      ))}
      <Button
        variant="outlined"
        onClick={() =>
          setRequestTypes((list) => {
            const title = "New request";
            return [
              ...list,
              {
                requestTypeId: 0,
                tenantKey: managementTenantKey(),
                code: uniqueRequestCode(title, list),
                title,
                confirmationTemplate: "I'll notify your treatment team.",
                isPatientSelectable: true,
                isEnabled: true,
                sortOrder: list.length + 1,
                escalationPolicyId: null,
                iconKey: null,
                priority: "Routine",
              },
            ];
          })
        }
      >
        Add request type
      </Button>
    </Stack>
  );
}
